// /bestsellers — which of the four tabs is showing, and what each one asks the grid for.
//
// The page is one grid with four sources. Every tab drives the SAME machinery the rest of
// the site uses — the `lm_*` hidden inputs, `search.load`, the filters sidebar, the thumb-size
// toggle — so the filters panel and the size toggle work on all four without any of them
// knowing the tabs exist.
//
// Two tabs hand the grid a pre-computed ranking (see `./ranking.ts`), and two give it a
// filter payload and let the server rank:
//
//   | tab      | how it ranks                                                    |
//   | -------- | --------------------------------------------------------------- |
//   | mine     | ranking: copies sold by this reseller, in the chosen period      |
//   | rec      | filter `sort-my-sales` — the existing "Based on your sales"      |
//   | upload   | ranking: clustered kNN over the reseller's uploaded images       |
//   | global   | filter `sort-bestsellers` — exactly what /bestsellers did before |
//
// `global` is deliberately the same payload the old page sent, down to the `best-seller-page`
// option that slightly randomises each page. It is the one tab whose output must not move.
//
// ⚠️ `sort-my-top-sellers` and `upload-bestsellers` are MARKERS, not server behaviour. Nothing
// on the server reads them — the ranking arrives as `rankedids` and the ORDER BY is skipped
// entirely. They are in the payload so a network trace says which tab produced it; do not go
// looking for the handler, and do not "wire them up". `filterBuilder` only reads keys it has
// a constant for, so an unrecognised one is inert.
import { getAction } from "../../actionRegistry";
import { getDomElement } from "../../utils/dom";
import { toastError } from "../../ui/messages";
import { api } from "../../api";
import { ensureSalesRecommendations, SALES_SORT_OPTION } from "../salesRecommendations";
import { currentTab } from "./routes";
import { setRanking } from "./ranking";
import { getUploads } from "./uploads";

export { BESTSELLER_TABS, DEFAULT_TAB, currentTab, tabFromPath, tabHref } from "./routes";
export type { BestsellerTab } from "./routes";

/**
 * Point the grid at a filter payload and reload it from the top.
 *
 * `lm_from` back to 0 and `lm_artworks_loaded` cleared, because both are per-result-set: a
 * tab switch that left them would page into the middle of the new ranking and then suppress
 * every artwork the previous tab had already shown.
 */
function loadWith(filters: Record<string, string>): void {
  const filtersEl = getDomElement<HTMLInputElement>("lm_filters");
  const fromEl = getDomElement<HTMLInputElement>("lm_from");
  const loadedEl = getDomElement<HTMLInputElement>("lm_artworks_loaded");
  const targetEl = getDomElement("collection-target");
  if (filtersEl) filtersEl.value = JSON.stringify([filters]);
  if (fromEl) fromEl.value = "0";
  if (loadedEl) loadedEl.value = "";
  // The grid appends; without this a tab switch shows the new rows under the old ones.
  if (targetEl) targetEl.innerHTML = "";
  const activeEl = getDomElement<HTMLInputElement>("lm_active");
  if (activeEl) activeEl.value = "1";
  getAction("load")?.();
}

/** The empty state, in place of the grid. Each tab has a different reason to be empty. */
function showEmpty(message: string): void {
  const target = getDomElement("collection-target");
  if (!target) return;
  const box = document.createElement("div");
  box.className = "bestsellers-empty";
  box.textContent = message;
  target.replaceChildren(box);
  // Stop the infinite scroll asking for page two of nothing.
  const activeEl = getDomElement<HTMLInputElement>("lm_active");
  if (activeEl) activeEl.value = "0";
}

/** The period selected in tab 1's dropdown. Empty before the server has filled it. */
function selectedPeriod(): string {
  return getDomElement<HTMLSelectElement>("bestsellers-period")?.value ?? "";
}

/**
 * Fill a `<select>` from the server's own list, keeping the current choice if it survives.
 *
 * ⚠️ The options come down with the DATA, not from the page, because they depend on the
 * reseller — the period list on their reporting cadence, the region default on their
 * country. Rebuilding the list on every load is what keeps the two from drifting apart.
 */
function fillSelect(id: string, options: { id: string; label: string }[], selected: string): void {
  const el = getDomElement<HTMLSelectElement>(id);
  if (!el) return;
  // Rebuilt only when the options actually changed, so re-running a tab does not reset a
  // dropdown the reader is mid-way through using.
  const same =
    el.options.length === options.length && options.every((o, i) => el.options[i]?.value === o.id);
  if (!same) {
    el.replaceChildren(
      ...options.map((o) => {
        const opt = document.createElement("option");
        opt.value = o.id;
        opt.textContent = o.label;
        return opt;
      })
    );
  }
  if (el.value !== selected) el.value = selected;
}

/**
 * Load "Your top sellers".
 *
 * The ranking is fetched here, once per tab visit or period change, and paged from the store
 * — see `./ranking.ts` for why it is not a join.
 */
async function loadMine(): Promise<void> {
  setRanking(null);
  try {
    const res = await api.search.topSellers({ period: selectedPeriod() });
    // The dropdown is the server's: a monthly filer gets four options, a quarterly one
    // three, and the same id means a different number of reports to each.
    fillSelect("bestsellers-period", res.periods, res.period);
    setRanking(res.ids);
    if (res.ids.length === 0) {
      showEmpty(
        res.period === "ever"
          ? "You have no reported sales yet, so there is nothing to rank. Try Global top sellers."
          : "No sales in that period. Try a longer one."
      );
      return;
    }
    // `hide-downloaded` is deliberately NOT set: these are artworks they have already sold,
    // so they own them — hiding what they own would empty the tab by definition.
    loadWith({ options: "", "sort-my-top-sellers": "1" });
  } catch (err: unknown) {
    setRanking(null);
    toastError(err instanceof Error ? err.message : "Could not load your top sellers");
  }
}

/** Load "Recommended" — the existing Browse sort, built on demand exactly as Browse does. */
async function loadRecommended(): Promise<void> {
  setRanking(null);
  // Same call, same lock, same failure handling as the Browse select. If it returns false
  // the list is not ready and loading the grid would silently show the Recommended order
  // instead, which reads as the tab doing nothing.
  const ready = await ensureSalesRecommendations();
  if (!ready) return;
  loadWith({ options: "", [SALES_SORT_OPTION]: "1", "hide-downloaded": "1" });
}

/**
 * Load "Upload bestsellers".
 *
 * Nothing is fetched until an image has been uploaded — as specified, and because the tab's
 * whole premise is "show me things like THESE". An empty grid of the catalogue's bestsellers
 * would answer a question nobody asked and look like the tab had ignored them.
 */
async function loadUploads(): Promise<void> {
  setRanking(null);
  const vecs = getUploads().map((u) => u.vecB64);
  if (vecs.length === 0) {
    showEmpty("Upload one or more of your bestsellers above to see similar artworks.");
    return;
  }
  try {
    const res = await api.search.imageClusters({ vecs });
    setRanking(res.ids);
    if (res.ids.length === 0) {
      showEmpty("Nothing in the catalogue came close to those images.");
      return;
    }
    loadWith({ options: "", "upload-bestsellers": "1" });
  } catch (err: unknown) {
    setRanking(null);
    toastError(err instanceof Error ? err.message : "Could not search for those images");
  }
}

/**
 * Load "Global top sellers" for the chosen region and period.
 *
 * Reads the precomputed ranking (`lib/buildRegionSales.ts`) rather than ordering on
 * `Artwork.sort_order_12_month_sale` as the old page did — that column is one fixed global
 * 12-month ranking and cannot answer "in Europe" or "in the last 3 months".
 */
async function loadGlobal(): Promise<void> {
  setRanking(null);
  const region = getDomElement<HTMLSelectElement>("bestsellers-region")?.value ?? "";
  const period = getDomElement<HTMLSelectElement>("bestsellers-global-period")?.value ?? "";
  try {
    const res = await api.search.globalTopSellers({ region, period });
    // The first load sends empty strings and the server resolves them — the region to the
    // reseller's own country. `res.region` and `res.period` are therefore what the ids are
    // FOR, so the dropdowns are set from those and always match the grid.
    fillSelect("bestsellers-region", res.regions, res.region);
    fillSelect("bestsellers-global-period", res.periods, res.period);
    setRanking(res.ids);
    if (res.ids.length === 0) {
      showEmpty("Nothing sold in that region and period.");
      return;
    }
    loadWith({ options: "", "global-top-sellers": "1" });
  } catch (err: unknown) {
    setRanking(null);
    showEmpty(err instanceof Error ? err.message : "Could not load global top sellers");
  }
}

/** Load whichever tab the URL names. The one entry point; everything else calls this. */
export function loadCurrentTab(): void {
  switch (currentTab()) {
    case "mine":
      void loadMine();
      break;
    case "rec":
      void loadRecommended();
      break;
    case "upload":
      void loadUploads();
      break;
    case "global":
      void loadGlobal();
      break;
  }
}
